import {
  OkResponseSchema,
  SessionListResponseSchema,
  SessionResponseSchema,
  type LoginRequest,
  type OkResponse,
  type PasswordChangeRequest,
  type RedeemCodeRequest,
  type ResetPasswordRequest,
  type SessionListResponse,
  type SessionResponse,
} from '@aroo/shared';
import { ApiError, apiDelete, apiGet, apiPost } from './client';

export const authKeys = {
  session: ['auth', 'session'] as const,
  sessions: ['auth', 'sessions'] as const,
};

/** The current session, or null when signed out (401 is the normal signed-out answer). */
export async function getSession(): Promise<SessionResponse | null> {
  try {
    return await apiGet('/auth/session', SessionResponseSchema);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export function login(body: LoginRequest): Promise<SessionResponse> {
  return apiPost('/auth/login', SessionResponseSchema, body);
}

export function logout(): Promise<OkResponse> {
  return apiPost('/auth/logout', OkResponseSchema);
}

export function logoutAll(): Promise<OkResponse> {
  return apiPost('/auth/logout-all', OkResponseSchema);
}

export function listSessions(): Promise<SessionListResponse> {
  return apiGet('/auth/sessions', SessionListResponseSchema);
}

export function revokeSession(id: string): Promise<OkResponse> {
  return apiDelete(`/auth/sessions/${encodeURIComponent(id)}`, OkResponseSchema);
}

export function changePassword(body: PasswordChangeRequest): Promise<OkResponse> {
  return apiPost('/auth/password/change', OkResponseSchema, body);
}

/** View as (D10): the session now runs read-only with this person's access. */
export function startViewAs(personId: string): Promise<SessionResponse> {
  return apiPost(`/view-as/${encodeURIComponent(personId)}`, SessionResponseSchema);
}

export function endViewAs(): Promise<SessionResponse> {
  return apiDelete('/view-as', SessionResponseSchema);
}

/** M5 (spec §3.4): always 200, whether or not the account exists. */
export function forgotPassword(identifier: string): Promise<OkResponse> {
  return apiPost('/auth/password/forgot', OkResponseSchema, { identifier });
}

/** Redeem the emailed link: 422 `invalid_token` or `password_*`, 403 `disabled`. */
export function resetPassword(body: ResetPasswordRequest): Promise<OkResponse> {
  return apiPost('/auth/password/reset', OkResponseSchema, body);
}

/** Staff number + manager code + new password: 422 `bad_code` or `password_*`. */
export function redeemCode(body: RedeemCodeRequest): Promise<OkResponse> {
  return apiPost('/auth/code/redeem', OkResponseSchema, body);
}
